import React, { useState, useEffect, useCallback } from 'react';
import {
  getNotificationPhone,
  updateNotificationPhone,
  getNotificationPhoneHistory,
  NotificationPhoneHistoryEntry,
} from '../../services/adminSettingsService';
import Loader from '../loader/Loader';
import Notification from '../toast/Notification';

const AdminNotificationPhoneSettings: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedByUserId, setUpdatedByUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [history, setHistory] = useState<NotificationPhoneHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [notification, setNotification] = useState<{ id: number; text: string; status: 'success' | 'error' | 'info' } | null>(null);

  const loadPhone = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getNotificationPhone();
      setPhone(data.phone);
      setUpdatedAt(data.updated_at || null);
      setUpdatedByUserId(data.updated_by_user_id ?? null);
      if (!newPhone) setNewPhone(data.phone);
    } catch (e) {
      console.error(e);
      showNotification('Ошибка загрузки настройки', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const data = await getNotificationPhoneHistory(50);
      setHistory(data.history);
    } catch (e) {
      console.error(e);
      showNotification('Ошибка загрузки истории', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPhone();
  }, [loadPhone]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const showNotification = (text: string, status: 'success' | 'error' | 'info') => {
    setNotification({ id: Date.now(), text, status });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newPhone.trim();
    if (!trimmed) {
      showNotification('Введите номер телефона', 'error');
      return;
    }
    try {
      setSaving(true);
      await updateNotificationPhone(trimmed);
      setPhone(trimmed);
      setNewPhone(trimmed);
      showNotification('Номер обновлён', 'success');
      loadHistory();
    } catch (e) {
      showNotification('Ошибка сохранения номера', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notification && (
        <Notification id={notification.id} text={notification.text} status={notification.status} duration={5000} />
      )}

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
          Номер для WhatsApp-уведомлений при заказе
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          На этот номер приходит уведомление о новом заказе. Можно менять в любой момент; в базе сохраняется история изменений.
        </p>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Текущий номер: <span className="font-mono">{phone || '—'}</span>
        </p>
        {updatedAt && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Обновлено: {new Date(updatedAt).toLocaleString('ru')}
            {updatedByUserId != null && ` (user ID: ${updatedByUserId})`}
          </p>
        )}

        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
          <label className="sr-only" htmlFor="admin-notification-phone-input">
            Новый номер телефона
          </label>
          <input
            id="admin-notification-phone-input"
            type="text"
            placeholder="+77001234567"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-w-[200px]"
          />
          <button
            type="submit"
            disabled={saving || newPhone.trim() === phone}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Сохранение...' : 'Сохранить номер'}
          </button>
        </form>
      </section>

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">История изменений</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Кто и когда менял номер. По этой истории можно проверить, кто последний обновлял настройку.
        </p>
        {historyLoading ? (
          <div className="flex justify-center py-4">
            <Loader />
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">История пуста</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Было</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Стало</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Кто (user ID)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Когда</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {history.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 text-sm font-mono text-gray-700 dark:text-gray-300">{entry.phone_before || '—'}</td>
                    <td className="px-4 py-2 text-sm font-mono text-gray-700 dark:text-gray-300">{entry.phone_after}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{entry.changed_by_user_id}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{entry.changed_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminNotificationPhoneSettings;
